import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteAuctionBidder } from 'api/campaigns';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import { useCurrentAuction } from 'customHooks/useCurrentAuction';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { isQueryKeyIndexMeta } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Bidder } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteBidderDialogProps extends AlertDialog.AlertDialogRootProps {
    bidder: Bidder;
}

const DeleteBidderDialog = (props: DeleteBidderDialogProps) => {
    const queryClient = useQueryClient();
    const { children, bidder, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const { data: auction } = useCurrentAuction();
    const { success, error } = useToast();
    const { goBack } = useDialogRoute();
    const campaignId = campaign.id;
    const auctionId = auction.id;

    const { mutate: deleteBidder } = useMutation({
        mutationFn: () => deleteAuctionBidder(campaignId, auctionId, bidder.id),
        onSuccess: () => {
            success('Bidder successfully deleted.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
                predicate: isQueryKeyIndexMeta,
                type: 'all',
            });
            goBack();
        },
        onError: (err: GivebutterAxiosError) => {
            error(err.response?.data?.message ?? 'Unable to delete bidder');
        },
    });

    return (
        <AlertDialog.Root {...rest} type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete bidder?</AlertDialog.Title>
                        <AlertDialog.Description>
                            You can't undo this action afterwards.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => deleteBidder()}>
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteBidderDialogTrigger = AlertDialog.Trigger;

const Root = DeleteBidderDialog;
const Trigger = DeleteBidderDialogTrigger;

export {
    Root,
    Trigger,
    //
    DeleteBidderDialog,
    DeleteBidderDialogTrigger,
};
